import {Injectable} from '@angular/core';
import {ProposalApiService} from '@emma-labs/shared/angular';
import {
  Beneficiary,
  ClientAgentAffiliationTime,
  Coverage,
  HealthAnswers,
  IdDocument,
  MinifiedProposal,
  PaginatedResponse,
  PaginationParams,
  Proposal,
  ProviderId,
  UserInfo,
} from '@emma-labs/shared/typescript';
import {first, mergeMap, Observable} from 'rxjs';
import {AuthService} from '../auth.service';

@Injectable({
  providedIn: 'root',
})
export class ProposalService {
  constructor(
    private readonly api: ProposalApiService,
    private readonly authService: AuthService,
  ) {}

  create(
    providerId: ProviderId,
    fnaId: string,
    submitter: UserInfo,
    healthAnswers: HealthAnswers,
  ): Observable<Proposal> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) =>
        this.api.create(session.id, atob(session.agent.id), providerId, fnaId, submitter, healthAnswers),
      ),
    );
  }

  createCopy(proposalId: string): Observable<Proposal> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.createCopy(session.id, proposalId)),
    );
  }

  get(proposalId: string): Observable<Proposal> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.getById(session.id, proposalId)),
    );
  }

  getAll(excludeSelfServe = false): Observable<MinifiedProposal[]> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.getAll(session.id, excludeSelfServe)),
    );
  }

  getAllPaginated(
    params: PaginationParams,
    excludeSelfServe = false,
  ): Observable<PaginatedResponse<MinifiedProposal>> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.getAllPaginated(session.id, params, excludeSelfServe)),
    );
  }

  search(filter: string, excludePrimaryAgentHuman: boolean): Observable<Proposal[]> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.search(session.id, filter, excludePrimaryAgentHuman)),
    );
  }

  updateSubmitter(proposalId: string, submitter: UserInfo): Observable<Proposal> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.updateSubmitter(session.id, proposalId, submitter, false)),
    );
  }

  updateHealthAnswers(proposalId: string, healthAnswers: HealthAnswers): Observable<Proposal> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.updateHealthAnswers(session.id, proposalId, healthAnswers)),
    );
  }

  updateProtection(proposalId: string, coverage: Coverage): Observable<Proposal> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.updateProtection(session.id, proposalId, coverage)),
    );
  }

  updateBeneficiaries(proposalId: string, beneficiaries: Beneficiary[]): Observable<Proposal> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.updateBeneficiaries(session.id, proposalId, beneficiaries)),
    );
  }

  updateJobCategoryId(proposalId: string, jobCategoryId: number): Observable<Proposal> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.updateJobCategoryId(session.id, proposalId, jobCategoryId)),
    );
  }

  addReplacementNotice(proposalId: string, file: File): Observable<Proposal> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.addReplacementNotice(session.id, proposalId, file)),
    );
  }

  removeReplacementNotice(proposalId: string): Observable<Proposal> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.removeReplacementNotice(session.id, proposalId)),
    );
  }

  updateIdDocument(proposalId: string, idDocument: IdDocument): Observable<Proposal> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.updateIdDocument(session.id, proposalId, idDocument)),
    );
  }

  updateAgentDisclosures(
    proposalId: string,
    clientAgentAffiliationTime: ClientAgentAffiliationTime,
    isAgentDisclosuresAccepted?: boolean,
  ): Observable<Proposal> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) =>
        this.api.updateAgentDisclosures(
          session.id,
          proposalId,
          clientAgentAffiliationTime,
          isAgentDisclosuresAccepted,
        ),
      ),
    );
  }

  updateNote(proposalId: string, note: string | undefined): Observable<Proposal> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.updateNote(session.id, proposalId, note)),
    );
  }

  send(proposalId: string): Observable<Proposal> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.send(session.id, proposalId)),
    );
  }

  delete(proposalId: string): Observable<void> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.delete(session.id, proposalId)),
    );
  }

  copyToBrokerage(proposalId: string): Observable<void> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.copyToBrokerage(session.id, proposalId)),
    );
  }
}
